import { useEffect, useState } from "react";
import classes from "./AdminPage.module.css";
import AdminLogin from "../components/admin/AdminLogin";
import EventForm from "../components/admin/EventForm";

const TOKEN_KEY = "admin_token";

export default function AdminPage() {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingEvent, setEditingEvent] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");

    useEffect(() => {
        fetch("/events.json")
            .then((res) => res.json())
            .then((data) => setEvents(data))
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, []);

    const handleLogin = (newToken) => {
        sessionStorage.setItem(TOKEN_KEY, newToken);
        setToken(newToken);
    };

    const handleLogout = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
    };

    const persistEvents = async (nextEvents) => {
        setSaving(true);
        setStatusMessage("");
        try {
            const res = await fetch("/api/events-save", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ events: nextEvents }),
            });
            if (!res.ok) {
                throw new Error("Save failed");
            }
            setEvents(nextEvents);
            setStatusMessage("Saved! The site will redeploy in a minute or two.");
            setShowForm(false);
            setEditingEvent(null);
        } catch {
            setStatusMessage("Save failed — your session may have expired. Try logging in again.");
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = (eventData) => {
        if (editingEvent) {
            persistEvents(events.map((e) => (e.id === editingEvent.id ? eventData : e)));
        } else {
            persistEvents([...events, eventData]);
        }
    };

    const handleDelete = (id) => {
        if (!window.confirm("Delete this event?")) return;
        persistEvents(events.filter((e) => e.id !== id));
    };

    if (!token) {
        return <AdminLogin onLogin={handleLogin} />;
    }

    return (
        <div className={classes.page}>
            <div className={classes.header}>
                <h1 className={classes.title}>Admin — Events</h1>
                <button className={classes.logoutButton} onClick={handleLogout}>
                    Log out
                </button>
            </div>

            {statusMessage && <p className={classes.status}>{statusMessage}</p>}

            {loading ? (
                <p>Loading events...</p>
            ) : showForm ? (
                <EventForm
                    initialEvent={editingEvent}
                    token={token}
                    onSubmit={handleSubmit}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingEvent(null);
                    }}
                    saving={saving}
                />
            ) : (
                <>
                    <button
                        className={classes.addButton}
                        onClick={() => {
                            setEditingEvent(null);
                            setShowForm(true);
                        }}
                    >
                        + Add new event
                    </button>
                    <ul className={classes.list}>
                        {events.map((event) => (
                            <li key={event.id} className={classes.listItem}>
                                <div>
                                    <p className={classes.listTitle}>{event.title}</p>
                                    <p className={classes.listMeta}>
                                        {event.date} · {event.location} · {event.status}
                                    </p>
                                </div>
                                <div className={classes.listActions}>
                                    <button
                                        onClick={() => {
                                            setEditingEvent(event);
                                            setShowForm(true);
                                        }}
                                    >
                                        Edit
                                    </button>
                                    <button onClick={() => handleDelete(event.id)} disabled={saving}>
                                        Delete
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
